import { currencyFormatter } from '../utils/currencyFormatter';

import { TIP_JAR_LAYOUTS } from './TipJar.constants';
import type { TipJarFeeRate, TipJarLayout } from './TipJar.types';

export type TipJarProgress = {
  hasGoal: boolean;
  isFunded: boolean;
  percentFunded: number | null;
  remainingCents: number | null;
};

const NO_GOAL_PROGRESS: TipJarProgress = { hasGoal: false, isFunded: false, percentFunded: null, remainingCents: null };

// Mirrors TipJar::Progress in Swarm, which renders the same progress into emails.
export const getTipJarProgress = (goalCents: number | null, raisedCents?: number): TipJarProgress => {
  if (goalCents === null || goalCents <= 0) return NO_GOAL_PROGRESS;

  const raised = raisedCents ?? 0;
  const isFunded = raised >= goalCents;

  return {
    hasGoal: true,
    isFunded,
    percentFunded: percentFunded(raised, goalCents, isFunded),
    remainingCents: Math.max(goalCents - raised, 0),
  };
};

// $2 of $500 is under half a percent. Flooring that to 0 reads as if nothing came in.
// A jar that is one cent short must not read 100%.
const percentFunded = (raised: number, goalCents: number, isFunded: boolean) => {
  if (isFunded) return 100;

  const percent = Math.round((raised * 100) / goalCents);
  if (raised > 0 && percent === 0) return 1;

  return Math.min(percent, 99);
};

// Mirrors TipJars::FeeEstimate in Swarm, which adds the same amount to the checkout, so the two must agree to the cent.
// Grosses the tip up so that what's left after the card's percentage and fixed fee is the whole tip, rounding up.
export const getTipJarFeeCoverCents = (tipCents: number, feeRate: TipJarFeeRate) => {
  const grossCents = Math.ceil(((tipCents + feeRate.fixed_cents) * 10_000) / (10_000 - feeRate.percent_bps));

  return grossCents - tipCents;
};

export const formatTipAmount = (amountCents: number, currency: string, locale?: string) => {
  const fractionDigits = amountCents % 100 === 0 ? 0 : 2;

  return currencyFormatter({
    value: amountCents / 100,
    currency,
    locale,
    minDigits: fractionDigits,
    maxDigits: fractionDigits,
  });
};

export const getCurrencySymbol = (currency: string, locale = 'en-US') =>
  new Intl.NumberFormat(locale, { style: 'currency', currency: currency.toUpperCase() })
    .formatToParts(0)
    .find((part) => part.type === 'currency')?.value ?? '';

/** Whole currency units: a reader types "7" for $7. */
export const parseWholeAmount = (input: string) => {
  const trimmed = input.trim();

  return /^\d+$/.test(trimmed) ? Number.parseInt(trimmed, 10) * 100 : null;
};

/** The jar's layout, falling back to the default banner for one this version doesn't know or a jar saved without one. */
export const getTipJarLayout = (layout: string | null | undefined): TipJarLayout =>
  TIP_JAR_LAYOUTS.find((known) => known === layout) ?? TIP_JAR_LAYOUTS[0];
